<script setup lang="ts">
/**
 * Personen-Avatar: ein Kreis mit den Initialen, eingefärbt in einem festen Ton je
 * Person. Kein hochgeladenes Bild — das braucht ein internes Werkzeug nicht, und
 * Initialen sind sofort da, ohne Upload und ohne Platzhalter-Gesicht.
 *
 * Weich gefärbt (wie die Status-Pillen: `--tint-*-bg` + `--tint-*-text`), damit der
 * Avatar zur ruhigen Gesamtwirkung passt statt als bunter Fleck herauszustechen.
 */
import { computed } from 'vue';
import { avatarTon, initialen } from '../lib/avatar';

const props = withDefaults(
  defineProps<{
    name: string;
    /** Stabiler Schlüssel für die Farbe — die Benutzer-ID. Fällt auf den Namen zurück. */
    schluessel?: string;
    size?: number;
  }>(),
  { schluessel: '', size: 32 }
);

const ton = computed(() => avatarTon(props.schluessel || props.name));
const kuerzel = computed(() => initialen(props.name));
</script>

<template>
  <span
    class="avatar"
    :class="`avatar--${ton}`"
    :style="{ width: `${size}px`, height: `${size}px`, fontSize: `${size * 0.4}px` }"
    aria-hidden="true"
  >
    {{ kuerzel }}
  </span>
</template>

<style scoped>
.avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: var(--radius-pill);
  font-weight: 600;
  line-height: 1;
  letter-spacing: 0.01em;
  background: var(--avatar-bg);
  color: var(--avatar-text);
  user-select: none;
}

.avatar--blue {
  --avatar-bg: var(--tint-blue-bg);
  --avatar-text: var(--tint-blue-text);
}
.avatar--green {
  --avatar-bg: var(--tint-green-bg);
  --avatar-text: var(--tint-green-text);
}
.avatar--purple {
  --avatar-bg: var(--tint-purple-bg);
  --avatar-text: var(--tint-purple-text);
}
.avatar--orange {
  --avatar-bg: var(--tint-orange-bg);
  --avatar-text: var(--tint-orange-text);
}
.avatar--amber {
  --avatar-bg: var(--tint-amber-bg);
  --avatar-text: var(--tint-amber-text);
}
</style>
